import { useState, useEffect, useRef } from 'react';

export default function OpeningsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const currentOpenings = [
    {
      title: 'Senior AI & Automation Specialist',
      department: 'Technology',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Lead the development of intelligent automation solutions and AI-powered digital experiences for our clients.',
      requirements: ['5+ years in AI/ML', 'Python, TensorFlow', 'Automation frameworks'],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Digital Marketing Strategist',
      department: 'Marketing',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Create and execute data-driven marketing strategies that drive measurable growth for diverse client portfolios.',
      requirements: ['3+ years in digital marketing', 'PPC & SEO expertise', 'Analytics proficiency'],
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Creative Brand Designer',
      department: 'Design',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Craft compelling visual identities and brand experiences that resonate with audiences and drive engagement.',
      requirements: ['4+ years in brand design', 'Adobe Creative Suite', 'Motion graphics skills'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Web3 & Blockchain Developer',
      department: 'Technology',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Pioneer next-generation decentralized applications and blockchain solutions for forward-thinking clients.',
      requirements: ['Smart contract development', 'Solidity, Web3.js', '2+ years blockchain experience'],
      gradient: 'from-orange-500 to-red-500'
    },
    {
      title: 'Project Manager - Digital Innovation',
      department: 'Operations',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Orchestrate complex digital transformation projects from conception to delivery, ensuring excellence at every step.',
      requirements: ['PMP/Agile certification', '5+ years project management', 'Tech project experience'],
      gradient: 'from-teal-500 to-blue-500'
    },
    {
      title: 'Content Strategy & Copywriter',
      department: 'Marketing',
      type: 'Full-time',
      location: 'Kochi, Kerala',
      description: 'Develop compelling content strategies and craft engaging copy that drives conversions and builds brand loyalty.',
      requirements: ['3+ years content strategy', 'SEO copywriting', 'Multi-platform experience'],
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section id="openings" ref={sectionRef} className="py-16 md:py-24 bg-black relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-12 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Current Openings
            </span>
          </h2>
          <h3 className="text-xl md:text-3xl text-white mb-6 font-semibold">
            Explore Your Opportunity.
          </h3>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We're always on the lookout for bright minds who are passionate about digital marketing, AI, design, 
            and innovation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-12">
          {currentOpenings.map((job, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 md:p-8 hover:border-orange-500/50 transition-colors duration-300 h-full">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-white mb-3">
                    {job.title}
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-3 py-1 bg-gradient-to-r ${job.gradient} text-white text-xs md:text-sm rounded-full font-medium`}>
                      {job.department}
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs md:text-sm rounded-full">
                      {job.type}
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-gray-300 text-xs md:text-sm rounded-full flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      {job.location}
                    </span>
                  </div>
                </div>

                <p className="text-gray-400 mb-6 leading-relaxed text-sm md:text-base">
                  {job.description}
                </p>

                <div className="mb-6">
                  <h5 className="text-white font-semibold mb-3 text-sm">Key Requirements:</h5>
                  <ul className="space-y-2">
                    {job.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="text-gray-400 text-sm flex items-center gap-2">
                        <i className="ri-check-line text-green-500"></i>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <button className="w-full px-6 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors duration-300">
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Don't See Your Perfect Role?</h3>
            <p className="text-gray-400 mb-6">
              We're always interested in connecting with talented individuals. Send us your resume!
            </p>
            <a 
              href="mailto:hello@hashadz.com"
              className="inline-block px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-full"
            >
              Submit Resume
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}